import React from 'react';
import { useTranslation } from 'react-i18next';

import IconBusiness from '@/assets/icon-business.svg?react';
import IconHouse from '@/assets/icon-house.svg?react';
import IconUnitArea from '@/assets/icon-unit-area.svg?react';
import IconVehicle from '@/assets/icon-vehicle.svg?react';

import type { SpaceCalculatorMode } from './calculator';

import classes from './styles.module.scss';

interface ModeOptionProps {
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    'data-i18n-key'?: string;
}

function ModeOption({
    icon,
    label,
    onClick,
    disabled = false,
    'data-i18n-key': i18nKey,
}: ModeOptionProps) {
    return (
        <button
            type="button"
            className={classes.modeOption}
            onClick={onClick}
            disabled={disabled}
        >
            <span className={classes.modeIcon} aria-hidden="true">{icon}</span>
            <span className={classes.modeLabel} data-i18n-key={i18nKey}>{label}</span>
        </button>
    );
}

interface ModeSelectorProps {
    isVehicleStorageAvailable: boolean;
    onSelectMode: (mode: Exclude<SpaceCalculatorMode, 'none'>) => void;
    onSelectVehicleStorage: () => void;
}

function ModeSelector({
    isVehicleStorageAvailable,
    onSelectMode,
    onSelectVehicleStorage,
}: ModeSelectorProps) {
    const { t } = useTranslation();

    return (
        <>
            <div className={classes.stepRow}>
                <div className={classes.stepText}>
                    <span>{t('standardUnitListing:spaceCalculator:stepOne')}</span>
                    {' '}
                    {t('standardUnitListing:spaceCalculator:stepOneTitle')}
                </div>
            </div>
            <div className={classes.modeOptions}>
                <ModeOption
                    icon={<IconHouse />}
                    label={t('standardUnitListing:spaceCalculator:movingHouse')}
                    onClick={() => onSelectMode('movingHouse')}
                />
                <ModeOption
                    icon={<IconUnitArea />}
                    label={t('standardUnitListing:spaceCalculator:extraSpace')}
                    onClick={() => onSelectMode('extraSpace')}
                />
                {isVehicleStorageAvailable ? (
                    <ModeOption
                        icon={<IconVehicle />}
                        label={t('standardUnitListing:spaceCalculator:vehicleStorage')}
                        onClick={onSelectVehicleStorage}
                    />
                ) : (
                    <ModeOption
                        icon={<IconVehicle />}
                        label={t('standardUnitListing:spaceCalculator:vehicleStorageUnavailable')}
                        disabled
                    />
                )}
                <ModeOption
                    icon={<IconBusiness />}
                    label={t('standardUnitListing:spaceCalculator:businessStorage')}
                    onClick={() => onSelectMode('businessStorage')}
                />
            </div>
            <p className={`${classes.helperText} ${classes.withArrow}`}>
                {t('standardUnitListing:spaceCalculator:clickBoxAbove')}
            </p>
        </>
    );
}

export default ModeSelector;
